import {
  collection, addDoc, updateDoc, doc, onSnapshot, writeBatch,
} from 'firebase/firestore';
import { db } from '../firebase';
import { DEMO_PATIENTS } from '../utils/data';

const SESSION_KEY = 'recallready_demo_patients';

// ---- Session storage helpers (demo mode) ----
function readSession() {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch (e) { /* fall through to seed */ }
  const seeded = DEMO_PATIENTS;
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(seeded));
  return seeded;
}

function writeSession(patients) {
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(patients));
}

export function resetDemoSession() {
  sessionStorage.removeItem(SESSION_KEY);
}

/**
 * One interface, two backends. Every Dashboard call goes through here so
 * demo mode and live mode never diverge in behavior — only in where the
 * data lands (Firestore vs sessionStorage, which clears on tab close).
 */
export function createPatientStore({ isDemo, practiceUid }) {
  return {
    // subscribe(cb) -> unsubscribe()
    subscribe(cb) {
      if (isDemo) {
        cb(readSession());
        // sessionStorage has no native change events within the same tab,
        // so the Dashboard calls refresh() itself after every mutation below.
        return () => {};
      }
      const colRef = collection(db, 'practices', practiceUid, 'patients');
      return onSnapshot(colRef, (snap) => {
        cb(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      });
    },

    async addPatient(data) {
      if (isDemo) {
        const patients = readSession();
        const newPatient = {
          id: `demo-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          visitHistory: [],
          lastContacted: null,
          createdAt: new Date().toISOString(),
          ...data,
        };
        // If editing, remove id from data to avoid duplicate
        const { id: _id, ...cleanData } = data;
        const finalPatient = { ...newPatient, ...cleanData };
        const next = [...patients, finalPatient];
        writeSession(next);
        return next;
      }
      await addDoc(collection(db, 'practices', practiceUid, 'patients'), {
        ...data, visitHistory: [], lastContacted: null, createdAt: new Date().toISOString(),
      });
      return null; // live mode updates via onSnapshot
    },

    // Bulk add for CSV import — batched for Firestore, single write for demo
    async addPatientsBulk(rows) {
      if (isDemo) {
        const patients = readSession();
        const newPatients = rows.map((data, i) => ({
          id: `demo-${Date.now()}-${i}-${Math.random().toString(36).slice(2, 7)}`,
          visitHistory: [],
          lastContacted: null,
          createdAt: new Date().toISOString(),
          ...data,
        }));
        const next = [...patients, ...newPatients];
        writeSession(next);
        return next;
      }
      // Firestore batches cap at 500 writes; chunk defensively
      const chunks = [];
      for (let i = 0; i < rows.length; i += 400) chunks.push(rows.slice(i, i + 400));
      for (const chunk of chunks) {
        const batch = writeBatch(db);
        chunk.forEach(data => {
          const ref = doc(collection(db, 'practices', practiceUid, 'patients'));
          batch.set(ref, { ...data, visitHistory: [], lastContacted: null, createdAt: new Date().toISOString() });
        });
        await batch.commit();
      }
      return null;
    },

    async updatePatient(id, updates) {
      if (isDemo) {
        const patients = readSession();
        const next = patients.map(p => {
          if (p.id === id) {
            // Deep merge for nested objects
            const merged = { ...p, ...updates };
            // Handle visitHistory specially
            if (updates.visitHistory !== undefined) {
              merged.visitHistory = updates.visitHistory;
            }
            return merged;
          }
          return p;
        });
        writeSession(next);
        return next;
      }
      await updateDoc(doc(db, 'practices', practiceUid, 'patients', id), updates);
      return null;
    },

    getSnapshotSync() {
      // demo-only convenience for synchronous reads after a mutation
      return isDemo ? readSession() : null;
    },
  };
}